import { z } from 'zod';
import { Id, IsoDateTime } from './common.ts';
import { KnowledgeSourceMetadata } from './knowledge.ts';

/** Where in a source an excerpt came from. */
export const EvidenceLocator = z
  .object({
    page: z.number().int().positive().optional(),
    section: z.string().optional(),
    heading: z.string().optional(),
    url: z.string().optional(),
    charStart: z.number().int().nonnegative().optional(),
    charEnd: z.number().int().nonnegative().optional(),
  })
  .meta({ id: 'EvidenceLocator' });
export type EvidenceLocator = z.infer<typeof EvidenceLocator>;

/** A cited excerpt from an authorised source. Retrieved text is data, never instructions. */
export const Evidence = z
  .object({
    id: z.string().min(1),
    sourceId: z.string().min(1),
    /** Source version as a string (integers are stringified). */
    sourceVersion: z.string().min(1),
    locator: EvidenceLocator,
    excerpt: z.string(),
    /** sha-256 hex of `excerpt`. */
    hash: z.string().min(1),
    retrievedAt: IsoDateTime,
    aclSubjects: z.array(z.string()).default([]),
    title: z.string().optional(),
    scope: z.string().optional(),
    score: z.number().optional(),
    /** True when the source was not verified within the freshness window. */
    stale: z.boolean().optional(),
    /** Added by 21 step 0 (§1b.7): the source's provenance metadata at retrieval time. */
    sourceMetadata: KnowledgeSourceMetadata.optional(),
  })
  .meta({ id: 'Evidence' });
export type Evidence = z.infer<typeof Evidence>;

/**
 * Added by 21 step 0 (brief 21 §1, 21c item 4; ADR-021): `GET /cases/{caseId}/evidence-index`, the canonical
 * evidence export of a case (links, documents, receipts, gate decisions, obligations, timeline). The export is
 * redacted for `viewer` first; `checksum` is sha256 over the canonical JSON of `{ caseId, caseRevision, exportedAt,
 * viewer, payload }` exactly as exported. A digest, not a signature (a real signature is out of scope in 21).
 */
export const EvidenceExport = z
  .object({
    caseId: Id,
    caseRevision: z.number().int().nonnegative(),
    exportedAt: IsoDateTime,
    /** Principal id the export was redacted for. */
    viewer: z.string().min(1),
    /** Canonical payload: `links`, `documents`, `receipts`, `gateDecisions`, `obligations`, `timeline`. */
    payload: z.record(z.string(), z.unknown()),
    checksum: z.object({ alg: z.literal('sha256'), value: z.string().regex(/^[a-f0-9]{64}$/) }),
  })
  .meta({ id: 'EvidenceExport' });
export type EvidenceExport = z.infer<typeof EvidenceExport>;

/** The object `EvidenceExport.checksum` is computed over (canonical JSON, sha256). */
export function evidenceChecksumInput(
  e: Pick<EvidenceExport, 'caseId' | 'caseRevision' | 'exportedAt' | 'viewer' | 'payload'>,
): Record<string, unknown> {
  return {
    caseId: e.caseId,
    caseRevision: e.caseRevision,
    exportedAt: e.exportedAt,
    viewer: e.viewer,
    payload: e.payload,
  };
}
